import { endOfMonth, format, startOfMonth, subDays, subMonths } from 'date-fns';
import { Download, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type DateRange } from 'react-day-picker';

import {
  STUDENT_PAYMENT_HISTORY_DATE_FIELD,
  type StudentPaymentHistoryDateField,
  useGetStudentPaymentHistory,
} from '~entities/student/api/get-student-payment-history';

import { DateRangePicker } from '~features/payment/ui/date-range-picker';

import { exportToCSV } from '~shared/helpers/csv';
import { parseToNoonDate } from '~shared/helpers/datetime';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

interface ExportStudentPaymentHistoryProps {
  studentId: string;
}

export const ExportStudentPaymentHistory = ({
  studentId,
}: ExportStudentPaymentHistoryProps) => {
  const [open, setOpen] = useState(false);
  const [exportTimePeriod, setExportTimePeriod] = useState('this-month');
  const [date, setDate] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [dateField, setDateField] = useState<StudentPaymentHistoryDateField>(
    STUDENT_PAYMENT_HISTORY_DATE_FIELD.PAID,
  );

  const { data, isFetching } = useGetStudentPaymentHistory({
    studentId,
    startDate: date?.from ? format(new Date(date.from), 'yyyy-MM-dd') : '',
    endDate: date?.to ? format(new Date(date.to), 'yyyy-MM-dd') : '',
    dateField,
    sortOrder: 'desc',
  });

  useEffect(() => {
    const today = new Date();
    if (exportTimePeriod === 'this-month') {
      setDate({
        from: startOfMonth(today),
        to: endOfMonth(today),
      });
    } else if (exportTimePeriod === 'last-month') {
      const previousMonth = subMonths(today, 1);

      const startDate = startOfMonth(previousMonth);
      const endDate = endOfMonth(previousMonth);

      setDate({
        from: startDate,
        to: endDate,
      });
    }
  }, [exportTimePeriod]);

  const handleExport = () => {
    setOpen(false);

    /** Leading tab forces Excel to keep the cell as text so it won't reformat as locale date (e.g. MM/DD/YY). */
    const asExcelTextDate = (value: string) => `\t${value}`;

    const headers = [
      'Due Date',
      'Paid Date',
      'Transaction ID',
      'Product',
      'Status',
      'Amount',
      'Discount',
      'Paid Amount',
    ];

    const formattedData = data?.map((invoice) => {
      const paidAt = parseToNoonDate(invoice.paymentDate);
      const dueAt = parseToNoonDate(invoice.dueDate);
      return {
        'Due Date': dueAt ? asExcelTextDate(format(dueAt, 'MM-dd-yyyy')) : '',
        'Paid Date': paidAt
          ? asExcelTextDate(format(paidAt, 'MM-dd-yyyy'))
          : '',
        'Transaction ID': invoice.invoiceId,
        Product: invoice.className,
        Status: invoice.status,
        // Keep export consistent with UI + outstanding calculations
        Amount: invoice.finalAmount,
        Discount: invoice.discount,
        'Paid Amount': invoice.paidAmount,
      };
    });

    exportToCSV(
      formattedData,
      headers,
      `payment-history-${new Date().toISOString()}.csv`,
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className='shrink-0'
          title='Export'
          aria-label='Export'
        >
          <Download />
          <span className='sr-only md:not-sr-only md:whitespace-nowrap'>
            Export
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export Transactions</DialogTitle>
          <DialogDescription>
            Download a CSV of transactions within a specific date range.
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-4'>
          <div className='grid gap-2'>
            <Label htmlFor='export-date-field'>Filter range by</Label>
            <Select
              value={dateField}
              onValueChange={(value) => {
                setDateField(value as StudentPaymentHistoryDateField);
              }}
            >
              <SelectTrigger id='export-date-field' className='w-full'>
                <SelectValue placeholder='Date type' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={STUDENT_PAYMENT_HISTORY_DATE_FIELD.DUE}>
                    Due date
                  </SelectItem>
                  <SelectItem value={STUDENT_PAYMENT_HISTORY_DATE_FIELD.PAID}>
                    Paid date
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className='grid gap-2'>
            <Label>Select Time Period</Label>
            <RadioGroup
              value={exportTimePeriod}
              onValueChange={setExportTimePeriod}
              className='grid gap-2'
            >
              <div className='flex items-center space-x-2 rounded-md border p-2'>
                <RadioGroupItem value='this-month' id='this-month' />
                <Label htmlFor='this-month' className='cursor-pointer'>
                  This Month
                </Label>
              </div>
              <div className='flex items-center space-x-2 rounded-md border p-2'>
                <RadioGroupItem value='last-month' id='last-month' />
                <Label htmlFor='last-month' className='cursor-pointer'>
                  Last Month
                </Label>
              </div>
              <div className='flex items-center space-x-2 rounded-md border p-2'>
                <RadioGroupItem value='custom' id='custom-range' />
                <Label htmlFor='custom-range' className='cursor-pointer'>
                  Custom Date Range
                </Label>
              </div>
            </RadioGroup>
          </div>

          {exportTimePeriod === 'custom' && (
            <DateRangePicker date={date} setDate={setDate} />
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button
            onClick={handleExport}
            disabled={
              (exportTimePeriod === 'custom' && (!date?.from || !date.to)) ||
              isFetching
            }
          >
            {isFetching && <Loader2 className='size-4 animate-spin' />}
            Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
